import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../../components/ui";
import { AssetTableItem } from "../../../types/types";
import { AssetTypeOptions } from "../../../options/options";
import { apiFetch } from "../../../services/apiClient";
import AssetMarkdownField from "./AssetMarkdownField";

export interface AssetCreateModalProps {
  isOpen: boolean;
  /** Pre-fill the parent dropdown. */
  parentId?: number | null;
  /** Full flat list of assets for the parent dropdown. */
  allAssets: AssetTableItem[];
  onClose: () => void;
  /** Called after successful creation with the new asset id. */
  onCreated: (_newAssetId: number) => void;
}

const EMPTY_FORM = {
  name: "",
  type: "",
  category: "",
  description: "",
  tags: "",
  parentId: "",
};

const AssetCreateModal: React.FC<AssetCreateModalProps> = ({
  isOpen,
  parentId,
  allAssets,
  onClose,
  onCreated,
}) => {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form and pre-fill parentId whenever modal opens
  useEffect(() => {
    if (isOpen) {
      setForm({ ...EMPTY_FORM, parentId: parentId != null ? String(parentId) : "" });
      setError(null);
    }
  }, [isOpen, parentId]);

  const set =
    (field: keyof typeof EMPTY_FORM) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.type || !form.category.trim()) {
      setError("Name, type, and category are required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const tags = form.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      const res = await apiFetch("/api/protected/assets/asset", {
        method: "POST",
        body: JSON.stringify({
          name: form.name.trim(),
          type: form.type,
          category: form.category.trim(),
          description: form.description,
          tags,
          parentId: form.parentId ? parseInt(form.parentId) : null,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to create asset");
      }
      const created = await res.json();
      onCreated(created.id as number);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalHeader onClose={onClose}>
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 bg-amber-400/15 border border-amber-400/30 rounded flex items-center justify-center flex-shrink-0">
            <FontAwesomeIcon icon={faPlus} className="text-amber-400 text-xs" />
          </div>
          <h2 className="font-semibold text-base">New Entry</h2>
        </div>
      </ModalHeader>
      <form onSubmit={handleSubmit} noValidate>
        <ModalBody>
          <div className="space-y-4">
            {error && (
              <div className="flex items-start gap-2 px-3 py-2 bg-red-900/30 border border-red-600/40 rounded text-red-300 text-xs">
                <FontAwesomeIcon icon={faXmark} className="flex-shrink-0 mt-0.5" />
                {error}
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">
                Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={set("name")}
                placeholder="e.g. The Sunken Keep"
                required
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-400/50"
              />
            </div>

            {/* Type + Category */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">
                  Type <span className="text-red-400">*</span>
                </label>
                <select
                  value={form.type}
                  onChange={set("type")}
                  required
                  className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-400/50"
                >
                  {AssetTypeOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">
                  Category <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.category}
                  onChange={set("category")}
                  placeholder="e.g. Dungeon, Noble…"
                  required
                  className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-400/50"
                />
              </div>
            </div>

            {/* Parent Asset */}
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Parent Asset</label>
              <select
                value={form.parentId}
                onChange={set("parentId")}
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-400/50"
              >
                <option value="">— None (root) —</option>
                {allAssets.map((a) => (
                  <option key={a.id} value={String(a.id)}>
                    {a.name} ({a.type})
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Description</label>
              <AssetMarkdownField
                value={form.description}
                onChange={(v) => setForm((prev) => ({ ...prev, description: v }))}
                allAssets={allAssets}
                onReferenceNavigate={() => {}}
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Tags</label>
              <input
                type="text"
                value={form.tags}
                onChange={set("tags")}
                placeholder="e.g. dungeon, boss, quest-hook (comma-separated)"
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-400/50"
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <span className="text-xs text-zinc-500">
            Fields marked <span className="text-red-400">*</span> are required
          </span>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-zinc-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-amber-400 text-zinc-950 text-sm font-bold rounded hover:bg-amber-300 transition-colors disabled:opacity-50"
            >
              {submitting ? "Creating…" : "Create Asset"}
            </button>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default AssetCreateModal;
